import React, { useEffect, useRef, useState } from 'react';
import { Steps, Card, Typography, message, Form, Button } from 'antd';
import { useLocation } from 'react-router-dom';
import { PlusCircleOutlined } from '@ant-design/icons';
import PatientForm from '../components/PatientForm';
import ModelRecommendation from '../components/ModelRecommendation';
import DoseCalculation from '../components/DoseCalculation';
import ReportPreview from '../components/ReportPreview';
import { useAgent } from '../contexts/AgentContext';
import { useResponsive } from '../contexts/ResponsiveContext';
import { drugAPI, intakeAPI } from '../services/api';
import { METHOD_CONFIG } from '../constants/methodConfig';
import type { AdministrationMode, AIExtractionSnapshot, Patient, Case, ModelMatchResult, DoseResult, RemedialDoseResult } from '../types';
import {
  getDefaultAdministrationMode,
  getSupportedAdministrationModes,
} from '../utils/doseMethodVisibility';

const { Title } = Typography;

const PKPD_METHOD_TARGET_PARAMETER: Record<string, string> = {
  'Target AUC': 'AUC24',
  'AUC/MIC': 'AUC24/MIC',
  'Target Concentration': 'Cmin',
  'Target Cmax': 'Cmax',
  '%fT>MIC': '%fT>MIC',
  'Cmax/MIC': 'Cmax/MIC',
};

const hasConfiguredPkpdTargets = (config: any): boolean => {
  const methods = Array.isArray(config?.calculation_methods) ? config.calculation_methods : [];
  const targets = Array.isArray(config?.targets) ? config.targets : [];
  return methods.some((method: string) => (
    method !== 'Remedial Dose'
    && Boolean(METHOD_CONFIG[method])
    && targets.some((target: any) => (
      target?.method === method
      || (PKPD_METHOD_TARGET_PARAMETER[method] && target?.parameter === PKPD_METHOD_TARGET_PARAMETER[method])
    ))
  ));
};

const modelSupportsAdministrationMode = (
  model: ModelMatchResult | null,
  mode: AdministrationMode,
): boolean => {
  if (!model) return true;
  const modes = Array.isArray(model.administration_modes) ? model.administration_modes : [];
  return modes.length > 0 ? modes.includes(mode) : mode === 'intermittent';
};

const NEW_CASE_STYLES = `
  .new-case-page {
    width: min(100%, 1180px);
    max-width: 1180px;
    min-width: 0;
    margin: 0 auto;
    padding: 10px 10px 28px;
  }

  .new-case-page__hero {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
    margin-bottom: 14px;
    padding: 0 4px;
  }

  .new-case-page__title.ant-typography {
    margin: 0 !important;
    color: #061529;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "PingFang SC", "Microsoft YaHei", sans-serif;
    font-size: clamp(24px, 1.9vw, 28px);
    font-weight: 800;
    letter-spacing: -0.02em;
    line-height: 1.08;
  }

  .new-case-page__steps.ant-steps {
    margin-bottom: 14px;
    padding: 0 6px;
  }

  .new-case-page__steps.ant-steps .ant-steps-item-title {
    color: #667085;
    font-size: 13px;
    font-weight: 600;
  }

  .new-case-page__steps.ant-steps .ant-steps-item-process .ant-steps-item-title,
  .new-case-page__steps.ant-steps .ant-steps-item-finish .ant-steps-item-title {
    color: #001529;
    font-weight: 700;
  }

  .new-case-page__steps.ant-steps .ant-steps-item-icon {
    width: 38px;
    height: 38px;
    margin-inline-end: 10px;
    background: linear-gradient(180deg, #EEF3F8 0%, #E6ECF3 100%);
    border: 3px solid #FFFFFF;
    box-shadow: 0 8px 20px rgba(10, 25, 47, 0.07);
  }

  .new-case-page__steps.ant-steps .ant-steps-item-wait .ant-steps-item-icon > .ant-steps-icon {
    color: #667085;
  }

  .new-case-page__steps.ant-steps .ant-steps-item-process .ant-steps-item-icon,
  .new-case-page__steps.ant-steps .ant-steps-item-finish .ant-steps-item-icon {
    background: #001529;
  }

  .new-case-page__steps.ant-steps .ant-steps-item-process .ant-steps-item-icon > .ant-steps-icon,
  .new-case-page__steps.ant-steps .ant-steps-item-finish .ant-steps-item-icon > .ant-steps-icon {
    color: #FFFFFF;
  }

  .new-case-page__steps.ant-steps .ant-steps-item-tail::after {
    height: 2px;
    background: #E5EAF1;
  }

  .new-case-page__steps.ant-steps .ant-steps-item-content {
    min-height: 34px;
  }

  .new-case-page__steps.ant-steps .ant-steps-item-finish .ant-steps-item-tail::after {
    background: #001529;
  }

  .new-case-page__panel.ant-card {
    border: none;
    background:
      radial-gradient(circle at top left, rgba(17, 92, 167, 0.04) 0%, transparent 28%),
      linear-gradient(180deg, #FCFDFF 0%, #FFFFFF 100%);
    border-radius: 28px;
    box-shadow: 0 18px 44px rgba(0, 21, 41, 0.06);
  }

  .new-case-page__panel.ant-card .ant-card-body {
    padding: 24px 24px 24px;
  }

  .new-case-page__ghost-btn.ant-btn {
    height: 38px;
    padding-inline: 16px;
    border-radius: 14px;
    border: 1px solid rgba(10, 25, 47, 0.08);
    background: linear-gradient(180deg, #FFFFFF 0%, #F9FBFE 100%);
    color: #0B2545;
    font-size: 13px;
    box-shadow: 0 10px 22px rgba(10, 25, 47, 0.035);
  }

  .new-case-page__steps-scroll {
    overflow-x: auto;
    margin: 0 -4px 14px;
    padding: 0 4px 2px;
  }

  .new-case-page__steps-track {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    min-width: 100%;
  }

  .new-case-page__step {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    color: #7A869A;
    white-space: nowrap;
    cursor: pointer;
  }

  .new-case-page__step-bullet {
    width: 34px;
    height: 34px;
    border-radius: 999px;
    background: #E7ECF3;
    border: 2px solid #FFFFFF;
    box-shadow: 0 6px 18px rgba(10, 25, 47, 0.07);
    color: #445266;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    font-weight: 700;
    flex-shrink: 0;
  }

  .new-case-page__step-label {
    font-size: 13px;
    font-weight: 700;
  }

  .new-case-page__step-line {
    width: 28px;
    height: 2px;
    border-radius: 999px;
    background: #D9E2ED;
    flex-shrink: 0;
  }

  .new-case-page__step--active .new-case-page__step-bullet,
  .new-case-page__step--done .new-case-page__step-bullet {
    background: #001529;
    color: #FFFFFF;
  }

  .new-case-page__step--active .new-case-page__step-label,
  .new-case-page__step--done .new-case-page__step-label {
    color: #001529;
  }

  .new-case-page__step--done + .new-case-page__step-line,
  .new-case-page__step--active + .new-case-page__step-line {
    background: #001529;
  }

  @media (max-width: 1024px) {
    .new-case-page__hero {
      align-items: flex-start;
      flex-direction: column;
    }

    .new-case-page__title.ant-typography {
      font-size: 24px;
    }

    .new-case-page__panel.ant-card .ant-card-body {
      padding: 18px;
    }
  }

  @media (max-width: 1024px) {
    .new-case-page--mobile .new-case-page__hero {
      gap: 12px;
      margin-bottom: 14px;
    }

    .new-case-page--mobile .new-case-page__title.ant-typography {
      font-size: 22px;
      line-height: 1.12;
    }

    .new-case-page--mobile .new-case-page__ghost-btn.ant-btn {
      width: 100%;
      height: 42px;
      justify-content: center;
    }

    .new-case-page--mobile .new-case-page__panel.ant-card {
      border-radius: 20px;
      box-shadow: 0 14px 32px rgba(0, 21, 41, 0.08);
    }

    .new-case-page--mobile .new-case-page__panel.ant-card .ant-card-body {
      padding: 16px;
    }
  }
`;

const NEW_CASE_STORAGE_KEY = 'precidose_new_case_draft';
const ADMIN_REPLAY_DRAFT_STORAGE_PREFIX = 'precidose_admin_replay_draft:';

interface NewCaseDraft {
  currentStep: number;
  patient: Patient | null;
  caseData: Case | null;
  selectedModel: ModelMatchResult | null;
  doseResult: DoseResult | RemedialDoseResult | null;
  calculationId: string;
  customInterpretation?: string;
  initialTab?: string;
  drugConfig: Record<string, any> | null;
  administrationMode?: AdministrationMode;
  patientFormValues?: Record<string, any>;
  aiExtractionSnapshot?: AIExtractionSnapshot | null;
  tdmReplayDraft?: TDMReplayDraft | null;
}

interface AdminReplayDraft {
  source?: string;
  sourceLabel?: string;
  calculationId?: string;
  caseId?: string;
  patientCode?: string;
  diagnosis?: string;
  gender?: string;
  drugName?: string;
  covariates?: Record<string, any>;
  derivedMetrics?: Record<string, any>;
  modelId?: string;
  modelDisplayName?: string;
  selectedMethod?: string;
  administrationMode?: AdministrationMode;
  dosingRegimens?: Array<Record<string, any>>;
  tdmSamples?: Array<Record<string, any>>;
  targetValues?: Record<string, number | undefined>;
  tdmValuesAreDisplayUnits?: boolean;
  pkFormulaInput?: Record<string, any>;
  calculationType?: string;
}

interface TDMReplayDraft {
  source?: string;
  sourceLabel?: string;
  calculationId?: string;
  modelId?: string;
  selectedMethod?: string;
  administrationMode?: AdministrationMode;
  dosingRegimens?: Array<Record<string, any>>;
  tdmSamples?: Array<Record<string, any>>;
      targetValues?: Record<string, number | undefined>;
  tdmValuesAreDisplayUnits?: boolean;
  pkFormulaInput?: Record<string, any>;
  calculationType?: string;
}

const NewCase: React.FC = () => {
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState(0);
  const [patient, setPatient] = useState<Patient | null>(null);
  const [caseData, setCaseData] = useState<Case | null>(null);
  const [selectedModel, setSelectedModel] = useState<ModelMatchResult | null>(null);
  const [doseResult, setDoseResult] = useState<DoseResult | RemedialDoseResult | null>(null);
  const [calculationId, setCalculationId] = useState<string>('');
  const [customInterpretation, setCustomInterpretation] = useState<string | undefined>(undefined);
  const [patientForm] = Form.useForm();
  const [initialTab, setInitialTab] = useState<string | undefined>(undefined);
  const [drugConfig, setDrugConfig] = useState<Record<string, any> | null>(null);
  const [administrationMode, setAdministrationMode] = useState<AdministrationMode>('intermittent');
  const [tdmReplayDraft, setTdmReplayDraft] = useState<TDMReplayDraft | null>(null);
  const patientFormValues = Form.useWatch([], patientForm);
  const hydratedRef = useRef(false);
  const replayAppliedRef = useRef<string | null>(null);

  const {
    updateContext,
    bindForm,
    bindResetWorkspace,
    setPendingFill,
    aiExtractionSnapshot,
    setAIExtractionSnapshot,
  } = useAgent();
  const { isMobileShell } = useResponsive();

  useEffect(() => {
    if (!drugConfig) return;
    const supportedModes = getSupportedAdministrationModes(drugConfig);
    if (supportedModes.includes(administrationMode)) return;
    setAdministrationMode(getDefaultAdministrationMode(drugConfig));
    setDoseResult(null);
    setCalculationId('');
    setSelectedModel(null);
    if (currentStep === 2) setCurrentStep(1);
  }, [administrationMode, currentStep, drugConfig]);

  // A restored model is only a client-side hint. Revalidate it against the
  // latest backend boundaries whenever the patient context or dose mode
  // changes, so an admin boundary edit cannot leave a stale model active.
  useEffect(() => {
    if (!hydratedRef.current || !caseData?.drug_name || !selectedModel) return;
    let alive = true;
    const validate = async () => {
      try {
        const context = await intakeAPI.context({
          drug_id: caseData.drug_name,
          covariates: caseData.covariates || {},
          confirmed_model_id: selectedModel.model_id,
          administration_mode: administrationMode,
        });
        if (!alive || context.confirmed_model_valid !== false) return;
        setSelectedModel(null);
        setDoseResult(null);
        setCalculationId('');
        setCustomInterpretation(undefined);
        setCurrentStep(1);
        message.warning('后台配置已变化，当前模型已不再适用于该患者，请重新匹配模型');
      } catch {
        // Calculation endpoints perform the authoritative validation. A
        // transient refresh failure must not discard an otherwise usable draft.
      }
    };
    void validate();
    return () => { alive = false; };
  }, [administrationMode, caseData, selectedModel]);

  useEffect(() => {
    try {
      const rawDraft = sessionStorage.getItem(NEW_CASE_STORAGE_KEY);
      if (!rawDraft) {
        hydratedRef.current = true;
        return;
      }

      const draft = JSON.parse(rawDraft) as NewCaseDraft;
      setCurrentStep(draft.currentStep ?? 0);
      setPatient(draft.patient ?? null);
      setCaseData(draft.caseData ?? null);
      setSelectedModel(draft.selectedModel ?? null);
      setDoseResult(draft.doseResult ?? null);
      setCalculationId(draft.calculationId ?? '');
      setCustomInterpretation(draft.customInterpretation);
      setInitialTab(draft.initialTab);
      setDrugConfig(draft.drugConfig ?? null);
      setAdministrationMode(draft.administrationMode ?? 'intermittent');
      setTdmReplayDraft(draft.tdmReplayDraft ?? null);
      setAIExtractionSnapshot(draft.aiExtractionSnapshot ?? null);
      if (draft.patientFormValues) {
        patientForm.setFieldsValue(draft.patientFormValues);
      }
    } catch {
      sessionStorage.removeItem(NEW_CASE_STORAGE_KEY);
    } finally {
      hydratedRef.current = true;
    }
  }, [patientForm, setAIExtractionSnapshot]);

  useEffect(() => {
    if (!hydratedRef.current) return;

    const draft: NewCaseDraft = {
      currentStep,
      patient,
      caseData,
      selectedModel,
      doseResult,
      calculationId,
      customInterpretation,
      initialTab,
      drugConfig,
      administrationMode,
      patientFormValues: patientFormValues || patientForm.getFieldsValue(true),
      aiExtractionSnapshot,
      tdmReplayDraft,
    };

    const hasContent = Boolean(
      calculationId
      || patient
      || caseData
      || selectedModel
      || doseResult
      || Object.keys(draft.patientFormValues || {}).length > 0,
    );

    if (!hasContent) {
      sessionStorage.removeItem(NEW_CASE_STORAGE_KEY);
      return;
    }

    sessionStorage.setItem(NEW_CASE_STORAGE_KEY, JSON.stringify(draft));
  }, [
    calculationId,
    administrationMode,
    caseData,
    currentStep,
    customInterpretation,
    doseResult,
    drugConfig,
    aiExtractionSnapshot,
    initialTab,
    patient,
    patientForm,
    patientFormValues,
    selectedModel,
    tdmReplayDraft,
  ]);

  useEffect(() => {
    if (!(location.state as any)?.reset) return;

    setCurrentStep(0);
    setPatient(null);
    setCaseData(null);
    setSelectedModel(null);
    setDoseResult(null);
    setCalculationId('');
    setCustomInterpretation(undefined);
    setInitialTab(undefined);
    setDrugConfig(null);
    setAdministrationMode('intermittent');
    setTdmReplayDraft(null);
    patientForm.resetFields();
    setPendingFill(null);
    setAIExtractionSnapshot(null);
    sessionStorage.removeItem(NEW_CASE_STORAGE_KEY);
    updateContext({
      patientContext: {},
      drugName: undefined,
      modelId: undefined,
      calcResult: undefined,
      calculationId: undefined,
      tdmData: undefined,
      drugConfig: undefined,
    });
  }, [location.state, patientForm, setAIExtractionSnapshot, setPendingFill, updateContext]);

  useEffect(() => {
    let replay = (location.state as any)?.adminReplayDraft as AdminReplayDraft | undefined;
    if (!replay) {
      const replayKey = new URLSearchParams(location.search).get('admin_replay_key');
      if (replayKey) {
        const storageKey = `${ADMIN_REPLAY_DRAFT_STORAGE_PREFIX}${replayKey}`;
        const rawReplay = localStorage.getItem(storageKey);
        if (rawReplay) {
          try {
            replay = JSON.parse(rawReplay) as AdminReplayDraft;
          } catch {
            message.error('复现数据读取失败，请从后台查询页重新打开');
          } finally {
            localStorage.removeItem(storageKey);
          }
        } else {
          message.warning('复现数据已失效，请从后台查询页重新打开');
        }
        window.history.replaceState(window.history.state, '', location.pathname);
      }
    }
    if (!replay) return;
    const signature = JSON.stringify(replay);
    if (replayAppliedRef.current === signature) return;
    replayAppliedRef.current = signature;

    const covariates = replay.covariates || {};
    const now = new Date().toISOString();
    const replayPatient: Patient = {
      id: replay.patientCode || replay.caseId || 'admin-replay',
      patient_id: replay.patientCode || replay.caseId || 'admin-replay',
      gender: replay.gender || covariates.gender,
      created_at: now,
      updated_at: now,
    };
    const replayCase: Case = {
      id: replay.caseId || '',
      patient_id: replayPatient.id,
      patient_code: replay.patientCode,
      drug_name: replay.drugName || covariates.drug_id || '',
      diagnosis: replay.diagnosis,
      covariates: covariates as any,
      derived_metrics: replay.derivedMetrics || {},
      status: 'admin_replay',
      created_at: now,
      updated_at: now,
    };
    const replayModel: ModelMatchResult | null = replay.modelId ? {
      model_id: replay.modelId,
      model_name: replay.modelDisplayName || replay.modelId,
      score: 0,
      population: '',
      age_range: '',
      weight_range: '',
      warnings: [],
      match_reasons: ['后台结构化详情复现'],
      boundary_status: {},
      publication_info: {
        pmid: '',
        doi: '',
        journal: '',
        year: '',
        first_author: replay.modelDisplayName || replay.modelId,
      },
      qualification_status: 'not_started',
      validation_status: '',
      model_status: 'active',
    } : null;

    setPatient(replayPatient);
    setCaseData(replayCase);
    setSelectedModel(replayModel);
    if (replay.administrationMode) {
      setAdministrationMode(replay.administrationMode as AdministrationMode);
    }
    setDoseResult(null);
    setCalculationId('');
    setCustomInterpretation(undefined);
     setInitialTab(replay.calculationType === 'pk_formula' ? 'pk_formula' : 'optimize');
    setCurrentStep(2);
    setTdmReplayDraft({
      source: replay.source,
      sourceLabel: replay.sourceLabel || '后台结构化详情',
      calculationId: replay.calculationId,
      modelId: replay.modelId,
      selectedMethod: replay.selectedMethod,
      administrationMode: replay.administrationMode,
      dosingRegimens: replay.dosingRegimens || [],
      tdmSamples: replay.tdmSamples || [],
      targetValues: replay.targetValues,
      pkFormulaInput: replay.pkFormulaInput,
      calculationType: replay.calculationType,
      tdmValuesAreDisplayUnits: replay.tdmValuesAreDisplayUnits,
    });
    patientForm.setFieldsValue({
      patient_id: replay.patientCode,
      drug_name: replay.drugName,
      diagnosis: replay.diagnosis,
      ...covariates,
    });
    setPendingFill(null);
    setAIExtractionSnapshot(null);
    if (replay.drugName) {
      drugAPI.get(replay.drugName).then(cfg => setDrugConfig(cfg)).catch(() => {});
    }
    sessionStorage.removeItem(NEW_CASE_STORAGE_KEY);
    message.success('已进入管理员复现模式，请核对后执行优化');
  }, [location.pathname, location.search, location.state, patientForm, setAIExtractionSnapshot, setPendingFill]);

  useEffect(() => {
    bindForm(patientForm);
    return () => bindForm(null);
  }, [patientForm, bindForm]);

  useEffect(() => {
    const stepMap = ['patient', 'model', 'dose', 'report'] as const;
    updateContext({
      workflowStep: stepMap[currentStep] || null,
      currentPath: location.pathname,
      patientContext: caseData?.covariates || {},
      drugName: caseData?.drug_name,
      modelId: selectedModel?.model_id,
      calcResult: (doseResult as any) || undefined,
      calculationId: calculationId || undefined,
      tdmData: doseResult && 'tdm_data' in doseResult ? doseResult.tdm_data : undefined,
      drugConfig: drugConfig || undefined,
    });
  }, [calculationId, caseData, currentStep, doseResult, drugConfig, location.pathname, selectedModel, updateContext]);

  const steps = [
    { title: '患者信息' },
    { title: '模型选择' },
    { title: '剂量计算' },
    { title: '临床报告' },
  ];

  const stepIntros = [
    { title: '新建病例' },
    { title: '模型选择' },
    { title: '个体化剂量计算' },
    { title: '报告预览' },
  ];

  const currentIntro = stepIntros[currentStep] || stepIntros[0];

  const handlePatientSubmit = (p: Patient, c: Case) => {
    setPatient(p);
    setCaseData(c);
    setCurrentStep(1);
    if (c.drug_name) {
      drugAPI.get(c.drug_name).then((cfg) => {
        setDrugConfig(cfg);
        setAdministrationMode(getDefaultAdministrationMode(cfg));
      }).catch(() => {});
    }
    message.success('患者信息保存成功');
  };

  const handleModelSelect = (model: ModelMatchResult) => {
    setSelectedModel(model);
    setInitialTab(undefined);
    setCurrentStep(2);
  };

  const handleAdministrationModeChange = (nextMode: AdministrationMode) => {
    if (nextMode === administrationMode) return;
    setAdministrationMode(nextMode);
    setDoseResult(null);
    setCalculationId('');
    setCustomInterpretation(undefined);
    if (!modelSupportsAdministrationMode(selectedModel, nextMode)) {
      setSelectedModel(null);
      setCurrentStep(1);
      message.info('当前模型不支持所选给药方式，请重新选择适用模型');
    }
  };

  const handleDoseCalculated = (result: DoseResult) => {
    setDoseResult(result);
    setCalculationId(result.calculation_id);
    setCustomInterpretation(result.clinical_interpretation);
    setCurrentStep(3);
  };

  const handleRemedialCalculated = (result: RemedialDoseResult) => {
    setDoseResult(result);
    setCalculationId(result.calculation_id);
    setCustomInterpretation(result.clinical_interpretation || undefined);
    setCurrentStep(3);
  };

  const handlePKFormulaCalculated = (result: any) => {
    setDoseResult(result);
    setCalculationId(result.calculation_id);
    setCustomInterpretation(result.clinical_interpretation);
    setCurrentStep(3);
  };

  const handleNewCase = React.useCallback(() => {
    setCurrentStep(0);
    setPatient(null);
    setCaseData(null);
    setSelectedModel(null);
    setDoseResult(null);
    setCalculationId('');
    setCustomInterpretation(undefined);
    setInitialTab(undefined);
    setDrugConfig(null);
    setAdministrationMode('intermittent');
    setTdmReplayDraft(null);
    patientForm.resetFields();
    setPendingFill(null);
    setAIExtractionSnapshot(null);
    sessionStorage.removeItem(NEW_CASE_STORAGE_KEY);
    updateContext({
      patientContext: {},
      drugName: undefined,
      modelId: undefined,
      calcResult: undefined,
      calculationId: undefined,
      tdmData: undefined,
      drugConfig: undefined,
    });
  }, [
    patientForm,
    setAIExtractionSnapshot,
    setPendingFill,
    updateContext,
  ]);

  useEffect(() => {
    bindResetWorkspace(handleNewCase);
    return () => bindResetWorkspace(null);
  }, [bindResetWorkspace, handleNewCase]);

  return (
    <div className={`new-case-page ${isMobileShell ? 'new-case-page--mobile' : ''}`}>
      <style>{NEW_CASE_STYLES}</style>

      <div className="new-case-page__hero">
        <div>
          <Title level={2} className="new-case-page__title">{currentIntro.title}</Title>
        </div>
        {currentStep > 0 && (
          <Button
            icon={<PlusCircleOutlined />}
            onClick={handleNewCase}
            className="new-case-page__ghost-btn"
          >
            新建病例
          </Button>
        )}
      </div>

      {isMobileShell ? (
        <div className="new-case-page__steps-scroll">
          <div className="new-case-page__steps-track">
            {steps.map((step, index) => (
              <React.Fragment key={step.title}>
                <div
                  className={`new-case-page__step ${index < currentStep ? 'new-case-page__step--done' : ''} ${index === currentStep ? 'new-case-page__step--active' : ''}`}
                  onClick={() => {
                    if (index < currentStep) setCurrentStep(index);
                  }}
                >
                  <span className="new-case-page__step-bullet">{index + 1}</span>
                  <span className="new-case-page__step-label">{step.title}</span>
                </div>
                {index < steps.length - 1 && <span className="new-case-page__step-line" />}
              </React.Fragment>
            ))}
          </div>
        </div>
      ) : (
        <Steps
          current={currentStep}
          items={steps}
          className="new-case-page__steps"
          onChange={(step) => {
            if (step < currentStep) setCurrentStep(step);
          }}
        />
      )}

      <Card variant="borderless" className="new-case-page__panel">
        {currentStep === 0 && (
          <PatientForm onSubmit={handlePatientSubmit} externalForm={patientForm} onClearWorkspace={handleNewCase} />
        )}

        {currentStep === 1 && caseData && (
          <ModelRecommendation
            covariates={caseData.covariates}
            drugName={caseData.drug_name}
            onSelect={handleModelSelect}
            administrationMode={administrationMode}
            supportedAdministrationModes={getSupportedAdministrationModes(drugConfig)}
            onAdministrationModeChange={handleAdministrationModeChange}
            supportsPkFormula={
              administrationMode === 'intermittent'
              && drugConfig?.supports_pk_formula === true
              && hasConfiguredPkpdTargets(drugConfig)
            }
            onFallback={() => {
              setSelectedModel(null);
              setInitialTab('pk_formula');
              setCurrentStep(2);
            }}
          />
        )}

        {currentStep === 2 && caseData && (
          <DoseCalculation
            caseId={caseData.id}
            selectedModel={selectedModel}
            covariates={caseData.covariates}
            drugName={caseData.drug_name}
            administrationMode={administrationMode}
            onAdministrationModeChange={handleAdministrationModeChange}
            onCalculated={handleDoseCalculated}
            onRemedialCalculated={handleRemedialCalculated}
            onPKFormulaCalculated={handlePKFormulaCalculated}
            initialTab={initialTab}
            tdmReplayDraft={tdmReplayDraft}
          />
        )}

        {currentStep === 3 && calculationId && (
          <ReportPreview
            calculationId={calculationId}
            doseResult={doseResult}
            customInterpretation={customInterpretation}
            patientCode={patient?.patient_id}
            drugName={caseData?.drug_name}
            adminReplay={tdmReplayDraft?.source === 'admin-data-query'}
          />
        )}
      </Card>
    </div>
  );
};

export default NewCase;
